import React from "react";
import ExtendedPicker from "components/Picker/ExtendedPicker";
import { useTranslation } from "react-i18next";
import {
  allPeriodValues,
  PeriodEnum,
  translatedValues,
  translateKey,
} from "common/enums/PeriodEnum";
import _ from "lodash";

interface PrescriptionPeriodPickerProps {
  /** Current period selection */
  selection: { number: number; unit: PeriodEnum };
  /** Callback when a value is changed. Name: name of the group that is changed. Value: the new value */
  handleChange?: (name: string, value: any) => void;
  /** Callback when the confirm checkmark button is clicked. */
  handleConfirm?: () => void;
}

const generateNumberRange = (start: number, end: number): Array<number> => {
  return Array.from(Array(1 + end - start).keys()).map((v) => start + v);
};

const maxNumberOfDays: number = 91;
const maxNumberOfWeeks: number = 13;
const maxNumberOfMonths: number = 3;

const PrescriptionPeriodPicker = ({
  selection,
  handleChange,
  handleConfirm,
}: PrescriptionPeriodPickerProps) => {
  const [t] = useTranslation();
  const translatedUnits = translatedValues(t);
  const optionGroups = () => {
    if (selection) {
      if (selection.unit === PeriodEnum.DAYS) {
        if (selection.number > maxNumberOfDays) {
          selection.number = maxNumberOfDays;
        }
        return {
          number: generateNumberRange(1, maxNumberOfDays),
          unit: translatedUnits,
        };
      } else if (selection.unit === PeriodEnum.WEEKS) {
        if (selection.number > maxNumberOfWeeks) {
          selection.number = maxNumberOfWeeks;
        }
        return {
          number: generateNumberRange(1, maxNumberOfWeeks),
          unit: translatedUnits,
        };
      } else if (selection.unit === PeriodEnum.MONTHS) {
        if (selection.number > maxNumberOfMonths) {
          selection.number = maxNumberOfMonths;
        }
        return {
          number: generateNumberRange(1, maxNumberOfMonths),
          unit: translatedUnits,
        };
      }
    }

    return {
      number: generateNumberRange(1, maxNumberOfDays),
      unit: translatedUnits,
    };
  };

  const handleTranslatedChange = (name: string, value: any) => {
    handleChange &&
      handleChange(
        name,
        name === "unit"
          ? _.nth(allPeriodValues(), translatedUnits.indexOf(value))
          : value
      );
  };

  const mapSelection = (selection: { number: number; unit: PeriodEnum }) => {
    return {
      number: selection.number,
      unit: t(translateKey(selection.unit)),
    };
  };

  return (
    <div style={{ height: "16rem", minHeight: "16rem" }}>
      <ExtendedPicker
        valueGroups={mapSelection(selection)}
        optionGroups={optionGroups()}
        handleConfirm={handleConfirm}
        handleChange={handleTranslatedChange}
      />
    </div>
  );
};

export default PrescriptionPeriodPicker;
